import { useEffect, useState } from "react";
import { Heart, Check, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
};

export function PrayerRequests({ streamId }) {
  const [items, setItems] = useState([]);

  const load = () => api.get(`/streams/${streamId}/prayer-requests`).then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const mark = async (p, prayed) => {
    try { await api.patch(`/streams/${streamId}/prayer-requests/${p.id}`, { prayed }); load(); }
    catch { toast.error("Couldn't update request"); }
  };

  const open = items.filter((p) => !p.prayed).length;

  return (
    <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-4" data-testid="prayer-requests">
      <h3 className="font-display text-lg font-semibold flex items-center gap-2">
        <Heart className="h-5 w-5 text-amber-400" /> Prayer requests
        <span data-testid="prayer-open-count" className="text-xs font-normal text-gray-400">{open} waiting</span>
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="prayer-empty">No prayer requests yet. Viewers can send them privately from the chat panel; you'll also get an email alert.</p>
      ) : (
        <div className="space-y-2 max-h-[28rem] overflow-y-auto">
          {items.map((p) => (
            <div key={p.id} data-testid={`prayer-item-${p.id}`} className={"rounded-lg border border-gray-800 bg-gray-950 p-3 transition-opacity " + (p.prayed ? "opacity-50" : "")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs"><span className="font-semibold text-amber-400">{p.name}</span><span className="text-gray-500 ml-2">{fmt(p.created_at)}</span></p>
                  <p className="text-sm text-gray-200 mt-1 whitespace-pre-wrap break-words">{p.request}</p>
                  {p.contact && <p className="text-xs text-gray-400 mt-1.5">Follow-up: {p.contact}</p>}
                </div>
                {p.prayed ? (
                  <button data-testid={`prayer-unmark-${p.id}`} onClick={() => mark(p, false)} className="shrink-0 text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"><RotateCcw className="h-3.5 w-3.5" /> Undo</button>
                ) : (
                  <button data-testid={`prayer-mark-${p.id}`} onClick={() => mark(p, true)} className="shrink-0 text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1 hover:bg-emerald-500/25"><Check className="h-3.5 w-3.5" /> Prayed</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
